import { normalizeRecipeMethod } from './recipeMethod.js'

/**
 * Cook Mode readiness for a saved recipe.
 *
 * @typedef {'guided' | 'unavailable'} CookModeKind
 *
 * @typedef {Object} RecipeCookReadiness
 * @property {boolean} canCook - User can enter Cook Mode.
 * @property {boolean} isGuidedReady - Step-by-step guided mode is available.
 * @property {CookModeKind} mode
 * @property {string[]} reasons - Human-readable notes for unavailable recipes.
 * @property {object[]} method - Normalized steps from {@link normalizeRecipeMethod}.
 * @property {number} ingredientCount
 * @property {number} stepCount
 * @property {number} timerStepCount
 */

const MIN_COOK_MODE_STEPS = 2
const MAX_AVERAGE_STEP_LENGTH = 500
const MAX_SINGLE_STEP_LENGTH = 900

function countIngredients(items) {
  if (!Array.isArray(items)) return 0
  return items.filter((item) => {
    if (!item || typeof item !== 'object') return false
    const name = String(item.item || item.name || item.item_name || item.ingredient || '').trim()
    return Boolean(name)
  }).length
}

function getStepLengthStats(method) {
  const lengths = method.map((step) => String(step?.text || '').trim().length)
  const maxStepLength = lengths.length ? Math.max(...lengths) : 0
  const averageStepLength = lengths.length
    ? lengths.reduce((sum, length) => sum + length, 0) / lengths.length
    : 0
  return { maxStepLength, averageStepLength }
}

/**
 * @param {object} options
 * @param {object} [options.listMetadata]
 * @param {object[]} [options.items]
 */
export function getRecipeCookReadiness({ listMetadata = {}, items = [] } = {}) {
  const meta = listMetadata && typeof listMetadata === 'object' ? listMetadata : {}
  const method = normalizeRecipeMethod(meta)
  const ingredientCount = countIngredients(items)
  const stepCount = method.length
  const timerStepCount = method.filter((step) => step?.timer).length
  const { maxStepLength, averageStepLength } = getStepLengthStats(method)

  const hasEnoughSteps = stepCount >= MIN_COOK_MODE_STEPS
  const stepsAreReadable = (
    stepCount > 0
    && maxStepLength <= MAX_SINGLE_STEP_LENGTH
    && averageStepLength <= MAX_AVERAGE_STEP_LENGTH
  )
  const isGuidedReady = hasEnoughSteps && stepsAreReadable
  const canCook = isGuidedReady

  /** @type {string[]} */
  const reasons = []
  if (stepCount === 0) {
    reasons.push('Cook Mode needs step-by-step directions.')
  } else if (!hasEnoughSteps) {
    reasons.push('Cook Mode needs at least two clear direction steps.')
  } else if (!stepsAreReadable) {
    reasons.push('Cook Mode needs shorter, clearer direction steps.')
  }

  return {
    canCook,
    isGuidedReady,
    mode: isGuidedReady ? 'guided' : 'unavailable',
    reasons,
    method,
    ingredientCount,
    stepCount,
    timerStepCount,
    maxStepLength,
    averageStepLength,
  }
}

/** CTA label on Recipe Detail. */
export function getCookModeCtaLabel(readiness) {
  if (!readiness?.canCook) return 'Cook Mode unavailable'
  return 'Start guided cooking →'
}

/** Short badge for recipe cards / detail chips. */
export function getCookModeBadgeLabel(readiness) {
  if (!readiness?.canCook) return null
  return 'Guided cooking ready'
}
